import { useCallback, useEffect, useId, useRef, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import { attachmentUrl, deleteAttachment, fetchAttachments, uploadAttachment, type Attachment, type SubjectType } from './api';
import './reviews.css';

const ACCEPT = ['image/jpeg', 'image/png', 'application/pdf'];
const MAX_BYTES = 5 * 1024 * 1024;

function Thumb({ learnerId, item }: { learnerId: string; item: Attachment }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    let live = true;
    attachmentUrl(learnerId, item.id).then((u) => {
      if (live) { url = u; setSrc(u); } else URL.revokeObjectURL(u);
    }).catch(() => undefined);
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [learnerId, item.id]);
  return src ? <img className="planner-evidence-thumb" src={src} alt={item.caption ?? item.filename} /> : <span className="planner-evidence-thumb is-loading" aria-hidden="true" />;
}

export default function Evidence({ learnerId, subjectType, subjectId, readOnly }: {
  learnerId: string; subjectType: SubjectType | 'REVIEW'; subjectId: string; readOnly?: boolean;
}) {
  const t = useT();
  const id = useId();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [list, setList] = useState<Attachment[] | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Attachment | null>(null);

  const load = useCallback(() => {
    fetchAttachments(learnerId, subjectType, subjectId).then(setList).catch((e) => setError(e instanceof Error ? e.message : t('The evidence could not be loaded just now.')));
  }, [learnerId, subjectType, subjectId, t]);

  useEffect(() => { load(); }, [load]);

  function choose(f: File | null) {
    setError(null);
    if (f && !ACCEPT.includes(f.type)) { setError(t('Add a photo (JPG or PNG) or a PDF.')); setFile(null); return; }
    if (f && f.size > MAX_BYTES) { setError(t('That file is too big. The limit is 5 MB.')); setFile(null); return; }
    setFile(f);
  }

  async function upload() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadAttachment(learnerId, subjectType, subjectId, file, caption.trim());
      setFile(null);
      setCaption('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('That file could not be added.'));
    } finally {
      setBusy(false);
    }
  }

  async function open(a: Attachment) {
    setError(null);
    const tab = window.open('', '_blank');
    if (!tab) { setError(t('Your browser blocked the new tab. Allow pop-ups for this site and try again.')); return; }
    try {
      const url = await attachmentUrl(learnerId, a.id);
      tab.location.href = url;
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      tab.close();
      setError(t('That file could not be opened just now.'));
    }
  }

  async function remove() {
    const a = removing;
    setRemoving(null);
    if (!a) return;
    try {
      await deleteAttachment(learnerId, a.id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That file could not be removed just now.'));
    }
  }

  return (
    <section className="planner-evidence" aria-labelledby={`${id}-h`}>
      <h4 id={`${id}-h`} className="planner-evidence-title">{t('Evidence')}</h4>
      {list && list.length === 0 && <p className="muted">{t('No files added yet.')}</p>}
      {list && list.length > 0 && (
        <ul className="planner-evidence-list">
          {list.map((a) => (
            <li key={a.id} className="planner-evidence-item">
              {a.contentType.startsWith('image/')
                ? <Thumb learnerId={learnerId} item={a} />
                : <span className="planner-evidence-thumb is-file" aria-hidden="true">{t('PDF')}</span>}
              <div className="planner-evidence-text">
                <strong>{a.caption ?? a.filename}</strong>
                {a.caption && <span className="muted">{a.filename}</span>}
              </div>
              <div className="toolbar">
                <button type="button" className="btn secondary" onClick={() => void open(a)}>{t('Open')}</button>
                {!readOnly && <button type="button" className="btn quiet-danger" onClick={() => setRemoving(a)}>{t('Remove')}</button>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {error && <div className="error" role="alert">{error}</div>}
      {!readOnly && (
        <div className="planner-evidence-add">
          <div className="field">
            <label htmlFor={`${id}-file`}>{t('Add a file')}</label>
            <input ref={fileRef} id={`${id}-file`} type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => choose(e.target.files?.[0] ?? null)} aria-describedby={`${id}-hint`} />
            <span className="field-hint" id={`${id}-hint`}>{t("Photos of work, charts or short notes as PDF. Up to 5 MB each. Please avoid photos that show a child's face unless you need them.")}</span>
          </div>
          <div className="field">
            <label htmlFor={`${id}-cap`}>{t('Caption (optional)')}</label>
            <input id={`${id}-cap`} maxLength={300} value={caption} onChange={(e) => setCaption(e.target.value)} />
          </div>
          <div className="toolbar">
            <button type="button" className="btn secondary" disabled={busy || !file} onClick={() => void upload()}>{busy ? t('Uploading…') : t('Upload')}</button>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={removing != null}
        title={t('Remove this file?')}
        message={t('The file will be removed for everyone. This cannot be undone.')}
        confirmLabel={t('Remove')}
        destructive
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
}
